/**
 * interactive-deduction (Phase 3-3, Task 6.4) Evidence-Based variant。
 *
 * 証拠主義のフォーマルな UI。淡々とした色合い (ネイビー / グレー) と ID 表記を
 * 前面に出す。文字情報中心、演出最小限。
 */

import { useState } from "react";
import { EvidenceSelector } from "./EvidenceSelector";
import { HpIndicator } from "./HpIndicator";
import type { DeductionPanelVariantProps } from "./types";

export function DeductionPanelEvidenceBased({
  contradictions,
  memories,
  attempts,
  onSubmit,
  disabled,
}: DeductionPanelVariantProps): JSX.Element {
  const [selectedContradictionId, setSelectedContradictionId] = useState<
    string | null
  >(contradictions[0]?.id ?? null);
  const [selectedMemoryKey, setSelectedMemoryKey] = useState<string | null>(
    null,
  );

  return (
    <section
      className="flex flex-col gap-4 p-5 bg-slate-900 border border-slate-600 rounded"
      data-testid="deduction-panel"
      data-variant="evidence-based"
    >
      <header className="flex justify-between items-center pb-3 border-b border-slate-700">
        <div>
          <h2 className="text-base font-mono tracking-wide text-slate-200">
            Deduction Round
          </h2>
          <p className="text-[10px] uppercase text-slate-500 mt-0.5">
            Submit evidence to resolve contradiction
          </p>
        </div>
        <HpIndicator attempts={attempts} />
      </header>

      <div className="flex flex-col gap-2">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          Contradictions
        </p>
        <div className="flex flex-col gap-1" role="radiogroup">
          {contradictions.map((c) => (
            <button
              key={c.id}
              type="button"
              role="radio"
              aria-checked={c.id === selectedContradictionId}
              disabled={disabled}
              onClick={() => setSelectedContradictionId(c.id)}
              data-testid={`contradiction-${c.id}`}
              className={[
                "text-left px-3 py-2 rounded font-mono text-sm",
                c.id === selectedContradictionId
                  ? "bg-slate-700 text-slate-100"
                  : "bg-slate-800/60 text-slate-300 hover:bg-slate-800",
              ].join(" ")}
            >
              <span className="text-[10px] text-slate-500">#{c.id}</span>{" "}
              {c.text}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          Evidence (Memory Fragments)
        </p>
        <EvidenceSelector
          fragments={memories}
          selectedKey={selectedMemoryKey}
          onSelect={setSelectedMemoryKey}
          disabled={disabled}
        />
      </div>

      <button
        type="button"
        disabled={
          disabled ||
          selectedContradictionId === null ||
          selectedMemoryKey === null
        }
        onClick={() => {
          if (selectedContradictionId !== null && selectedMemoryKey !== null) {
            onSubmit({
              contradictionId: selectedContradictionId,
              memoryFragmentKey: selectedMemoryKey,
            });
          }
        }}
        data-testid="deduction-submit"
        className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-100 font-mono text-sm rounded disabled:opacity-40"
      >
        Submit Evidence
      </button>
    </section>
  );
}
